import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { format } from "date-fns";
import { Trash2, Edit2, Plus, X, Package, Send, Mail, Clock, Heart, Search, Eye, AlertTriangle } from "lucide-react";
import { useSortableTable } from "@/hooks/useSortableTable";
import { safeFormat } from "@/lib/dateUtils";
import { EditableCell } from "@/components/EditableCell";
import { PageControls } from "@/components/PageControls";
import AdminAppointmentDialog from "@/components/AdminAppointmentDialog";
import AppointmentDetailsDialog from "@/components/AppointmentDetailsDialog";

const STATUS_OPTIONS = [
  { value: 'Confirmed', label: 'Confirmed', className: 'bg-green-100 text-green-700' },
  { value: 'Pending', label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  { value: 'Completed', label: 'Completed', className: 'bg-blue-100 text-blue-700' },
  { value: 'Cancelled', label: 'Cancelled', className: 'bg-red-100 text-red-700' },
  { value: 'No-show', label: 'No-show', className: 'bg-gray-100 text-gray-700' },
];
const PAY_STATUS_OPTIONS = [
  { value: 'Unpaid', label: 'Unpaid', className: 'bg-red-100 text-red-700' },
  { value: 'Deposit', label: 'Deposit paid', className: 'bg-amber-100 text-amber-700' },
  { value: 'Paid', label: 'Fully paid', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'Complimentary', label: 'Complimentary', className: 'bg-purple-100 text-purple-700' },
  { value: 'Refunded', label: 'Refunded', className: 'bg-gray-100 text-gray-700' },
];
const PAY_TYPE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank', label: 'Bank' },
  { value: 'gift_card', label: 'Gift Card' },
];

export default function Appointments() {
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus') || '';
  const rowRefs = useRef({});
  const [appointments, setAppointments] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [detailsAppt, setDetailsAppt] = useState(null);
  const [resendPrompt, setResendPrompt] = useState(null);
  // Product usage
  const [prodDialog, setProdDialog] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [prodSelections, setProdSelections] = useState([]);
  const [savingProducts, setSavingProducts] = useState(false);
  const [prodSearch, setProdSearch] = useState('');

  const load = async () => {
    try {
      const params = new URLSearchParams({ limit: String(pageSize), skip: String((page - 1) * pageSize) });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (search.trim()) params.append('search', search.trim());
      const res = await axios.get(`${API}/appointments?${params}`);
      setAppointments(res.data.appointments || []);
      setTotal(res.data.total || 0);
    } catch (e) {  }
  };

  useEffect(() => { setPage(1); }, [filterStatus, search]);
  useEffect(() => { load(); }, [filterStatus, search, page, pageSize]);

  // Deep-link: when ?focus=<appointmentId>, fetch that appointment, pre-fill the
  // search box with the client's email so the row appears, and scroll to highlight it.
  useEffect(() => {
    if (!focusId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/appointments/${focusId}`);
        const a = res.data;
        if (cancelled || !a) return;
        // Prefer email — most unique. Fallback to name.
        const term = (a.client_email || a.client_name || '').trim();
        if (term) setSearch(term);
        setFilterStatus('all');
      } catch (e) { /* not found */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  // Once data is loaded, scroll the focused row into view & flash highlight
  useEffect(() => {
    if (!focusId || !appointments.length) return;
    const el = rowRefs.current[focusId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusId, appointments]);

  const enriched = appointments.map(a => ({
    ...a,
    _date_sort: a.appointment_datetime,
    _services_text: (a.services || []).map(s => s.name).join(', ')
  }));

  // A "length mismatch" means the admin corrected verified_nail_length (what
  // they actually saw in person) to something different from what the
  // client selected/booked — a retrospective flag for spotting dishonest
  // re-selections over time, without needing to open every appointment.
  const getLengthMismatch = (appt) => {
    const bookedLength = (appt.services || []).find(s => s.selected_length)?.selected_length;
    if (!bookedLength || !appt.verified_nail_length || appt.verified_nail_length === bookedLength) return null;
    return { booked: bookedLength, verified: appt.verified_nail_length };
  };

  const { sortedData, SortHeader } = useSortableTable(enriched, '_date_sort', 'desc');

  // Inline save for single field
  const saveInline = async (apptId, field, value) => {
    try {
      await axios.put(`${API}/appointments/${apptId}`, { [field]: value });
      setAppointments(prev => prev.map(a => a.id === apptId ? { ...a, [field]: value } : a));
      toast.success('Updated');
    } catch (e) { toast.error('Failed to save'); }
  };

  // Full edit dialog
  const openEdit = (appt) => {
    setEditForm({
      client_name: appt.client_name || '',
      client_email: appt.client_email || '',
      client_phone: appt.client_phone || '',
      appointment_datetime: appt.appointment_datetime ? appt.appointment_datetime.slice(0, 16) : '',
      services: (appt.services || []).map(s => ({ ...s })),
      total_price: appt.total_price || 0,
      discount: appt.discount || 0,
      notes: appt.notes || '',
      booking_status: appt.booking_status || 'Confirmed',
      payment_status: appt.payment_status || 'Unpaid',
      payment_type: appt.payment_type || 'cash',
      staff_name: appt.staff_name || '',
    });
    setEditDialog(appt);
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    try {
      const res = await axios.put(`${API}/appointments/${editDialog.id}`, {
        ...editForm,
        total_price: parseFloat(editForm.total_price) || 0,
        discount: parseFloat(editForm.discount) || 0,
      });
      toast.success('Appointment updated');
      const savedApptId = editDialog.id;
      const keyChanged = res.data?._key_fields_changed;
      setEditDialog(null);
      load();
      // Prompt resend if key fields changed
      if (keyChanged) {
        setResendPrompt(savedApptId);
      }
    } catch (e) { toast.error('Failed to update'); }
  };

  const resendConfirmation = async (apptId) => {
    try {
      const res = await axios.post(`${API}/appointments/${apptId}/resend-confirmation`);
      if (res.data.success) {
        toast.success(`Updated confirmation sent to ${res.data.sent_to}`);
      } else {
        toast.error(res.data.error || 'Failed to send');
      }
    } catch (e) { toast.error('Failed to resend'); }
    setResendPrompt(null);
  };

  const updateService = (idx, field, value) => {
    setEditForm(prev => {
      const services = [...prev.services];
      services[idx] = { ...services[idx], [field]: value };
      const newTotal = services.reduce((s, svc) => s + (parseFloat(svc.price) || 0), 0) - (parseFloat(prev.discount) || 0);
      return { ...prev, services, total_price: newTotal };
    });
  };

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Appointments</h1>
          <p className="text-sm text-muted-foreground">{total} total · Click any cell to edit inline</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 rounded-xl"><SelectValue placeholder="Filter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <AdminAppointmentDialog onCreated={load} />
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search client, email, service…"
          className="pl-9 rounded-xl h-9 text-sm"
        />
      </div>

      <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <SortHeader field="client_name">Client</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground">Email</th>
                <SortHeader field="_date_sort">Date & Time</SortHeader>
                <SortHeader field="_services_text">Services</SortHeader>
                <SortHeader field="total_price">Total</SortHeader>
                <SortHeader field="booking_status">Status</SortHeader>
                <SortHeader field="payment_status">Payment</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground text-xs">Voucher</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Notes</th>
                <th className="text-left p-3 font-medium text-muted-foreground w-16">Act.</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(appt => (
                <tr
                  key={appt.id}
                  ref={(el) => { if (el) rowRefs.current[appt.id] = el; }}
                  data-testid={`appt-row-${appt.id}`}
                  className={`border-b border-border/50 hover:bg-muted/10 transition-colors ${focusId === appt.id ? 'bg-amber-50 ring-2 ring-amber-300 ring-inset' : ''}`}
                >
                  <td className="p-1.5 pl-3">
                    <EditableCell 
                      value={appt.client_name}
                      field="client_name"
                      onSave={(f, v) => saveInline(appt.id, f, v)}
                      className="font-medium"
                    />
                  </td>
                  <td className="p-1.5">
                    <EditableCell 
                      value={appt.client_email}
                      field="client_email"
                      onSave={(f, v) => saveInline(appt.id, f, v)}
                      className="text-xs"
                      placeholder="email"
                    />
                  </td>
                  <td className="p-1.5 whitespace-nowrap text-xs">{safeFormat(appt.appointment_datetime, 'EEE d MMM, HH:mm')}</td>
                  <td className="p-1.5 text-xs max-w-[160px]">
                    <span className="truncate block">{(appt.services || []).map(s => s.name).join(', ')}</span>
                    {(() => {
                      const mismatch = getLengthMismatch(appt);
                      if (!mismatch) return null;
                      return (
                        <span
                          data-testid={`appt-length-mismatch-${appt.id}`}
                          title={`Client selected "${mismatch.booked}" at booking, but admin recorded "${mismatch.verified}" as the actual verified length.`}
                          className="inline-flex items-center gap-0.5 mt-1 text-[9px] font-medium text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        >
                          <AlertTriangle size={9} className="shrink-0" /> Length mismatch
                        </span>
                      );
                    })()}
                  </td>
                  <td className="p-1.5">
                    <EditableCell 
                      value={appt.total_price}
                      field="total_price"
                      type="number"
                      onSave={(f, v) => saveInline(appt.id, f, v)}
                      displayRender={(v) => <span className="font-medium tabular-nums">£{(parseFloat(v) || 0).toFixed(2)}</span>}
                    />
                  </td>
                  <td className="p-1.5">
                    <EditableCell 
                      value={appt.booking_status}
                      field="booking_status"
                      options={STATUS_OPTIONS}
                      onSave={(f, v) => saveInline(appt.id, f, v)}
                    />
                  </td>
                  <td className="p-1.5">
                    <div className="space-y-1">
                      <EditableCell 
                        value={appt.payment_status}
                        field="payment_status"
                        options={PAY_STATUS_OPTIONS}
                        onSave={(f, v) => saveInline(appt.id, f, v)}
                      />
                      {appt.payment_status === 'Deposit' && appt.deposit_amount > 0 && (
                        <div className="space-y-0.5">
                          <div className="text-[10px] text-emerald-700 font-semibold leading-tight">
                            Paid: £{(appt.deposit_amount || 0).toFixed(2)}
                          </div>
                          {(appt.voucher_amount_applied || 0) > 0 && (
                            <div className="text-[10px] text-pink-600 font-semibold leading-tight" data-testid={`appt-voucher-deduction-${appt.id}`}>
                              Voucher: -£{(appt.voucher_amount_applied || 0).toFixed(2)}
                            </div>
                          )}
                          {(appt.promo_amount_applied || 0) > 0 && (
                            <div className="text-[10px] text-purple-600 font-semibold leading-tight" data-testid={`appt-promo-deduction-${appt.id}`}>
                              Promo: -£{(appt.promo_amount_applied || 0).toFixed(2)}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-amber-700 font-semibold leading-tight" data-testid={`appt-balance-due-${appt.id}`}>
                              Bal: £{Math.max(0, (appt.total_price || 0) - (appt.deposit_amount || 0) - (appt.voucher_amount_applied || 0) - (appt.promo_amount_applied || 0)).toFixed(2)}
                            </span>
                            <button
                              title="Mark balance as collected"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/payments/mark-paid/${appt.booking_id}`, {});
                                  toast.success('Marked as fully paid');
                                  load();
                                } catch { toast.error('Failed'); }
                              }}
                              className="text-[10px] text-emerald-600 underline hover:no-underline"
                            >
                              Mark paid
                            </button>
                          </div>
                        </div>
                      )}
                      {appt.payment_status === 'Paid' && appt.deposit_amount > 0 && (
                        <div className="text-[10px] text-emerald-700 font-semibold leading-tight">
                          Paid: £{(appt.deposit_amount || 0).toFixed(2)} ✓
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-1.5">
                    {appt.voucher_code_applied ? (
                      <div className="text-xs">
                        <Badge className="bg-pink-100 text-pink-700 text-[9px] rounded-full">{appt.voucher_code_applied}</Badge>
                        <p className="text-[10px] text-green-600 font-medium mt-0.5">-£{(appt.voucher_amount_applied || 0).toFixed(2)}</p>
                      </div>
                    ) : appt.promo_code_applied ? (
                      <div className="text-xs">
                        <Badge className="bg-purple-100 text-purple-700 text-[9px] rounded-full">{appt.promo_code_applied}</Badge>
                        <p className="text-[10px] text-green-600 font-medium mt-0.5">-£{(appt.promo_amount_applied || 0).toFixed(2)}</p>
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="p-1.5 max-w-[140px]">
                    <EditableCell 
                      value={appt.notes}
                      field="notes"
                      onSave={(f, v) => saveInline(appt.id, f, v)}
                      className="text-xs"
                      placeholder="Add note..."
                    />
                  </td>
                  <td className="p-1.5">
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/inventory/products?active_only=true&limit=500`);
                          setAllProducts(res.data.products || []);
                        } catch (e) { setAllProducts([]); }
                        setProdSelections([{ product_id: '', uses_consumed: 1 }]);
                        setProdSearch('');
                        setProdDialog(appt);
                      }} className="h-6 w-6 rounded-lg" title="Add products used">
                        <Package size={11} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => resendConfirmation(appt.id)} className="h-6 w-6 rounded-lg" title="Resend confirmation email">
                        <Mail size={11} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={async () => {
                        try { const r = await axios.post(`${API}/appointments/${appt.id}/resend-reminder`); r.data.success ? toast.success(`Reminder sent to ${r.data.sent_to}`) : toast.error(r.data.error); } catch(e) { toast.error('Failed'); }
                      }} className="h-6 w-6 rounded-lg" title="Resend 24hr reminder">
                        <Clock size={11} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={async () => {
                        try { const r = await axios.post(`${API}/appointments/${appt.id}/resend-thankyou`); r.data.success ? toast.success(`Thank you sent to ${r.data.sent_to}`) : toast.error(r.data.error); } catch(e) { toast.error('Failed'); }
                      }} className="h-6 w-6 rounded-lg" title="Resend thank you email">
                        <Heart size={11} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDetailsAppt(appt)} className="h-6 w-6 rounded-lg" title="View details" data-testid={`appt-view-btn-${appt.id}`}>
                        <Eye size={11} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(appt)} className="h-6 w-6 rounded-lg" title="Full edit">
                        <Edit2 size={11} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={async () => {
                        if (!window.confirm(`Delete ${appt.client_name}?`)) return;
                        try { await axios.delete(`${API}/appointments/${appt.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
                      }} className="h-6 w-6 rounded-lg text-destructive" title="Delete">
                        <Trash2 size={11} />
                      </Button>
                    </div>
                    {(appt.products_used || []).length > 0 && (
                      <p className="text-[9px] text-muted-foreground mt-0.5">{(appt.products_used || []).length} products · £{(appt.total_product_cost || 0).toFixed(2)}</p>
                    )}
                  </td>
                </tr>
              ))}
              {sortedData.length === 0 && (
                <tr><td colSpan="10" className="p-8 text-center text-muted-foreground">No appointments found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <PageControls page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />

      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>Full Edit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Client Name</Label><Input value={editForm.client_name || ''} onChange={e => setEditForm(p => ({...p, client_name: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
              <div><Label className="text-xs">Email</Label><Input value={editForm.client_email || ''} onChange={e => setEditForm(p => ({...p, client_email: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={editForm.client_phone || ''} onChange={e => setEditForm(p => ({...p, client_phone: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date & Time</Label><Input type="datetime-local" value={editForm.appointment_datetime || ''} onChange={e => setEditForm(p => ({...p, appointment_datetime: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
              <div><Label className="text-xs">Staff</Label><Input value={editForm.staff_name || ''} onChange={e => setEditForm(p => ({...p, staff_name: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold">Services</Label>
                <Button variant="ghost" size="sm" onClick={() => setEditForm(p => ({...p, services: [...p.services, {service_id:'',name:'',duration_minutes:30,price:0}]}))} className="rounded-lg h-7 text-xs"><Plus size={12} className="mr-1" /> Add</Button>
              </div>
              {(editForm.services || []).map((svc, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-muted/30 rounded-xl mb-1">
                  <Input value={svc.name || ''} onChange={e => updateService(i, 'name', e.target.value)} className="rounded-lg h-8 text-sm flex-1" placeholder="Service" />
                  <Input type="number" value={svc.duration_minutes || 0} onChange={e => updateService(i, 'duration_minutes', parseInt(e.target.value) || 0)} className="rounded-lg h-8 text-sm w-16" /><span className="text-xs">min</span>
                  <span className="text-xs">£</span><Input type="number" step="0.01" value={svc.price || 0} onChange={e => updateService(i, 'price', parseFloat(e.target.value) || 0)} className="rounded-lg h-8 text-sm w-20" />
                  <Button variant="ghost" size="icon" onClick={() => setEditForm(p => ({...p, services: p.services.filter((_,j)=>j!==i)}))} className="h-7 w-7 rounded-lg text-destructive"><X size={12} /></Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Discount (£)</Label><Input type="number" step="0.01" value={editForm.discount || 0} onChange={e => setEditForm(p => ({...p, discount: parseFloat(e.target.value)||0, total_price: p.services.reduce((s,sv)=>s+(parseFloat(sv.price)||0),0)-(parseFloat(e.target.value)||0)}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
              <div><Label className="text-xs">Total (£)</Label><Input type="number" step="0.01" value={editForm.total_price || 0} onChange={e => setEditForm(p => ({...p, total_price: parseFloat(e.target.value)||0}))} className="mt-1 rounded-xl h-9 text-sm font-semibold" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Status</Label><Select value={editForm.booking_status||'Confirmed'} onValueChange={v=>setEditForm(p=>({...p,booking_status:v}))}><SelectTrigger className="mt-1 rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(s=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Payment</Label><Select value={editForm.payment_status||'Unpaid'} onValueChange={v=>setEditForm(p=>({...p,payment_status:v}))}><SelectTrigger className="mt-1 rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent>{PAY_STATUS_OPTIONS.map(p=><SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Method</Label><Select value={editForm.payment_type||'cash'} onValueChange={v=>setEditForm(p=>({...p,payment_type:v}))}><SelectTrigger className="mt-1 rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent>{PAY_TYPE_OPTIONS.map(p=><SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={editForm.notes||''} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))} className="mt-1 rounded-xl text-sm" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={saveEdit} className="rounded-xl">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Confirmation Prompt */}
      <Dialog open={!!resendPrompt} onOpenChange={() => setResendPrompt(null)}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>Appointment Updated</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Would you like to send an updated confirmation to the client?</p>
            <p className="text-xs text-muted-foreground">This will send a new email with the updated details and an updated calendar invite.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setResendPrompt(null)} className="rounded-xl">Skip</Button>
            <Button onClick={() => resendConfirmation(resendPrompt)} className="rounded-xl">
              <Send size={14} className="mr-1" /> Send Updated Confirmation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Usage Dialog */}
      <Dialog open={!!prodDialog} onOpenChange={() => { setProdDialog(null); setProdSearch(''); }}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>
              <Package size={18} className="inline mr-2 text-primary" />
              Products Used - {prodDialog?.client_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Service: {(prodDialog?.services || []).map(s => s.name).join(', ')}</p>
            
            {/* Existing products on this appointment */}
            {(prodDialog?.products_used || []).length > 0 && (
              <div className="p-2 bg-muted/30 rounded-xl">
                <p className="text-[10px] font-medium text-muted-foreground mb-1">Already recorded:</p>
                {prodDialog.products_used.map((pu, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5 group">
                    <span>{pu.product_name} ({pu.uses_consumed} uses)</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-primary font-medium">£{(pu.line_cost || 0).toFixed(2)}</span>
                      <button 
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/appointments/${prodDialog.id}/products/${i}`);
                            toast.success(`${pu.product_name} removed (stock refunded)`);
                            // Refresh appointment data
                            const res = await axios.get(`${API}/appointments/${prodDialog.id}`);
                            setProdDialog(prev => ({ ...prev, products_used: res.data.products_used || [] }));
                            load();
                          } catch (e) { toast.error('Failed to remove'); }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10 rounded p-0.5 transition-opacity"
                        title="Remove product (refunds stock)"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Product picker — search + multi-select */}
            <div className="space-y-3">
              <Label className="text-xs font-semibold">Add Products Used</Label>

              {/* Persistent search bar */}
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={prodSearch}
                  onChange={e => setProdSearch(e.target.value)}
                  placeholder="Search and tick products…"
                  className="pl-8 h-8 rounded-lg text-xs"
                />
              </div>

              {/* Scrollable product list with checkboxes */}
              <div className="max-h-52 overflow-y-auto rounded-xl border divide-y divide-border bg-card">
                {allProducts.filter(p =>
                  p.active && (!prodSearch || p.name.toLowerCase().includes(prodSearch.toLowerCase()))
                ).length === 0 && (
                  <p className="text-xs text-muted-foreground p-3 text-center">No products match</p>
                )}
                {allProducts.filter(p =>
                  p.active && (!prodSearch || p.name.toLowerCase().includes(prodSearch.toLowerCase()))
                ).map(p => {
                  const alreadySelected = prodSelections.some(s => s.product_id === p.id);
                  const isLow = (p.uses_on_hand || 0) <= (p.reorder_level_uses || 10);
                  return (
                    <button
                      key={p.id}
                      className={`w-full text-left px-3 py-2.5 text-xs transition-colors flex items-center gap-2.5 ${
                        alreadySelected ? 'bg-primary/8 text-primary' : 'hover:bg-muted/30'
                      }`}
                      onClick={() => {
                        if (alreadySelected) {
                          // Deselect — remove from selections
                          setProdSelections(prev => prev.filter(s => s.product_id !== p.id));
                        } else {
                          // Select — add with default 1 use
                          setProdSelections(prev => [...prev, { product_id: p.id, uses_consumed: 1 }]);
                        }
                      }}
                    >
                      {/* Checkbox indicator */}
                      <div className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 transition-colors ${
                        alreadySelected ? 'bg-primary border-primary' : 'border-muted-foreground/40'
                      }`}>
                        {alreadySelected && (
                          <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        )}
                      </div>
                      <span className="flex-1 font-medium truncate">{p.name}</span>
                      <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${
                        isLow ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {p.uses_on_hand || 0} uses
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Selected products — quantity adjustment */}
              {prodSelections.filter(s => s.product_id).length > 0 && (
                <div className="space-y-2 pt-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    {prodSelections.filter(s => s.product_id).length} product{prodSelections.filter(s => s.product_id).length !== 1 ? 's' : ''} selected — adjust quantities
                  </p>
                  {prodSelections.map((sel, i) => {
                    if (!sel.product_id) return null;
                    const product = allProducts.find(p => p.id === sel.product_id);
                    if (!product) return null;
                    return (
                      <div key={sel.product_id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/20 border">
                        <span className="flex-1 text-xs font-medium truncate">{product.name}</span>
                        {product.cost_per_use > 0 && (
                          <span className="text-[10px] text-primary shrink-0 font-medium">
                            £{((product.cost_per_use || 0) * sel.uses_consumed).toFixed(2)}
                          </span>
                        )}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => setProdSelections(prev => prev.map((s, j) => j === i ? {...s, uses_consumed: Math.max(1, (s.uses_consumed || 1) - 1)} : s))}
                            className="w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs font-bold leading-none transition-colors"
                          >−</button>
                          <span className="w-6 text-center text-xs font-semibold tabular-nums">{sel.uses_consumed}</span>
                          <button
                            onClick={() => setProdSelections(prev => prev.map((s, j) => j === i ? {...s, uses_consumed: (s.uses_consumed || 1) + 1} : s))}
                            className="w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs font-bold leading-none transition-colors"
                          >+</button>
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0">uses</span>
                        <Button variant="ghost" size="icon"
                          onClick={() => setProdSelections(prev => prev.filter((_, j) => j !== i))}
                          className="h-5 w-5 rounded-lg text-destructive shrink-0"
                        ><X size={9} /></Button>
                      </div>
                    );
                  })}

                  {/* Total cost */}
                  <div className="flex justify-between pt-2 border-t text-xs font-medium">
                    <span>Total cost:</span>
                    <span className="text-primary">
                      £{prodSelections.reduce((sum, sel) => {
                        const p = allProducts.find(pr => pr.id === sel.product_id);
                        return sum + (p ? (p.cost_per_use || 0) * sel.uses_consumed : 0);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProdDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={async () => {
              const valid = prodSelections.filter(s => s.product_id);
              if (valid.length === 0) { toast.error('Select at least one product'); return; }
              setSavingProducts(true);
              try {
                await axios.post(`${API}/appointments/${prodDialog.id}/products`, { products: valid });
                toast.success('Products recorded & stock deducted');
                setProdDialog(null);
                load();
              } catch (e) { toast.error('Failed to save'); }
              setSavingProducts(false);
            }} disabled={savingProducts} className="rounded-xl">
              {savingProducts ? 'Saving...' : 'Save & Deduct Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shared Appointment Details (used from Calendar + this page) */}
      <AppointmentDetailsDialog
        appointment={detailsAppt}
        open={!!detailsAppt}
        onOpenChange={(o) => { if (!o) setDetailsAppt(null); }}
        onSaved={(updated) => { if (updated) setDetailsAppt(updated); load(); }}
      />
    </div>
  );
}
